import { inject, Injectable, Provider } from "@angular/core";
import { Observable, Observer, Subscription } from "rxjs";
import { EProductCategoryImageType, IProductCategoryTreeGetQuery, IProductCategoryTreeGetQueryResult, ProductCategoryClient, ProductCategoryDetailGetQuery, ProductCategoryDetailGetQueryResult, ProductCategoryImageGetQuery, ProductCategoryImageGetQueryResult, ProductCategoryTreeGetQuery } from "../../INTEGRA_Shop_Client";
import { IProductCategoryApiService, IProductCategoryApiServiceToken } from "./product-category-api-service.interface";
import { IComparisonService, IComparisonServiceToken } from "../../infrastructure/comparison/comparison-service.interface";

@Injectable({
  providedIn: 'root'
})
export class ProductCategoryApiService implements IProductCategoryApiService {
  private readonly productCategoryClient: ProductCategoryClient = inject(ProductCategoryClient);
  private readonly comparisonService: IComparisonService = inject(IComparisonServiceToken);

  private productCategoryImageSubscription: Subscription | undefined;
  private productCategoryTreeGetSubscription: Subscription | undefined;
  private lastProductCategoryImageGetQuery: ProductCategoryImageGetQuery | undefined;

  public productCategoryTreeGet(
    params?: Partial<IProductCategoryTreeGetQuery>,
    allowCancellation: boolean | undefined = true
  ): Observable<IProductCategoryTreeGetQueryResult> {
    let query = new ProductCategoryTreeGetQuery({
      categoryKey: params?.categoryKey,
    });

    // no cancellation: normal request
    if (!allowCancellation) {
      return this.productCategoryClient.productCategoryTreeGet(query);
    }

    // cancellation: kill old subscription
    if (this.productCategoryTreeGetSubscription) {
      this.productCategoryTreeGetSubscription.unsubscribe();
      this.productCategoryTreeGetSubscription = undefined;
    }

    // new observable with manuel subscription management
    return new Observable<IProductCategoryTreeGetQueryResult>(observer => {
      this.productCategoryTreeGetSubscription = this.productCategoryClient.productCategoryTreeGet(query).subscribe({
        next: (result) => observer.next(result),
        error: (error) => {
          observer.error(error);
          this.productCategoryTreeGetSubscription = undefined;
        },
        complete: () => {
          observer.complete();
          this.productCategoryTreeGetSubscription = undefined;
        }
      });

      // cleanup upon component unsubscribed
      return () => {
        if (this.productCategoryTreeGetSubscription) {
          this.productCategoryTreeGetSubscription.unsubscribe();
          this.productCategoryTreeGetSubscription = undefined;
        }
      };
    });
  }

  public productCategoryImageGet(
    productCategoryImageType: EProductCategoryImageType,
    categoryKeyList?: string[] | undefined,
    useCancellationLogic: boolean | undefined = true): Observable<ProductCategoryImageGetQueryResult> {
    let query = new ProductCategoryImageGetQuery({
      categoryKeyList: categoryKeyList,
      productCategoryImageType: productCategoryImageType
    });

    if (useCancellationLogic
      && this.productCategoryImageSubscription != undefined
      && this.comparisonService.equalsGenericDeepSlow(this.lastProductCategoryImageGetQuery, query)
    ) {
      this.productCategoryImageSubscription.unsubscribe();
    }

    this.lastProductCategoryImageGetQuery = query;

    return new Observable((observer: Observer<ProductCategoryImageGetQueryResult>) => {
      this.productCategoryImageSubscription = this.productCategoryClient.productCategoryImageGet(query).subscribe(
        result => {
          observer.next(result);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  public productCategoryDetailGet(categoryKey?: string | undefined): Observable<ProductCategoryDetailGetQueryResult> {
    let query = new ProductCategoryDetailGetQuery({
      categoryKey: categoryKey,
    });
    return this.productCategoryClient.productCategoryDetailGet(query);
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const ProductCategoryApiServiceProvider: Provider[] = [
  { provide: ProductCategoryClient },
  { provide: IProductCategoryApiServiceToken, useExisting: ProductCategoryApiService }
];
